import { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'wnc_preview_mode';

// Compute initial preview state synchronously so first render already has the correct flag.
// Avoids a race where the initial (false) render fires a live fetch that arrives after the
// preview fetch and overwrites draft state in the page component.
function computeInitial() {
  if (typeof window === 'undefined') return { preview: false, isAdmin: false };
  const params = new URLSearchParams(window.location.search);
  const urlFlag = params.get('preview');
  let preview = false;
  if (urlFlag === '1') {
    sessionStorage.setItem(SESSION_KEY, '1');
    preview = true;
  } else if (urlFlag === '0') {
    sessionStorage.removeItem(SESSION_KEY);
    preview = false;
  } else {
    preview = sessionStorage.getItem(SESSION_KEY) === '1';
  }
  const isAdmin = !!localStorage.getItem('admin_token');
  return { preview, isAdmin };
}

/**
 * usePreviewMode — detects `?preview=1` in the URL and persists across navigation
 * via sessionStorage (only while the tab is open). Also exposes an admin-auth check
 * so we can gracefully fall back to live content if the visitor isn't a signed-in admin.
 * The initial state is computed synchronously so the first render already has the correct flag.
 */
export function usePreviewMode() {
  const initial = computeInitial();
  const [preview, setPreview] = useState(initial.preview);
  const [isAdmin, setIsAdmin] = useState(initial.isAdmin);

  // React StrictMode may double-mount; keep state in sync with URL/session on remount
  useEffect(() => {
    const current = computeInitial();
    setPreview(current.preview);
    setIsAdmin(current.isAdmin);
  }, []);

  const exitPreview = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setPreview(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('preview');
    window.location.href = url.toString();
  }, []);

  return { preview: preview && isAdmin, previewRequested: preview, isAdmin, exitPreview };
}

/**
 * fetchWithPreview — helper that fetches a public content endpoint, adding
 * `?preview=1` + admin Bearer token when we're in preview mode. Endpoints that
 * understand the flag (e.g. /api/homepage/public) will return draft content;
 * others transparently fall back to live.
 */
export async function fetchWithPreview(url, { previewEnabled }) {
  const finalUrl = new URL(url, window.location.origin);
  const headers = {};
  if (previewEnabled) {
    finalUrl.searchParams.set('preview', '1');
    const token = localStorage.getItem('admin_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(finalUrl.toString(), { headers });
  const data = res.ok ? await res.json() : null;
  const isDraft = !!(data && data.preview);
  return { data, isDraft };
}

/**
 * fetchWithDraft — deprecated in favor of fetchWithPreview when the backend
 * endpoint supports the preview flag natively. Kept for pages that use the
 * generic content_drafts collection.
 */
export async function fetchWithDraft({ liveUrl, draftKey, merge, previewEnabled }) {
  const liveRes = await fetch(liveUrl);
  const live = liveRes.ok ? await liveRes.json() : null;
  if (!previewEnabled || !draftKey) return { data: live, isDraft: false, draftMeta: null };
  try {
    const token = localStorage.getItem('admin_token');
    if (!token) return { data: live, isDraft: false, draftMeta: null };
    const dr = await fetch(`${API}/api/content/drafts/${encodeURIComponent(draftKey)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (dr.status === 404) return { data: live, isDraft: false, draftMeta: null };
    if (!dr.ok) return { data: live, isDraft: false, draftMeta: null };
    const draft = await dr.json();
    const merged = merge ? merge(live, draft.data) : draft.data;
    return { data: merged, isDraft: true, draftMeta: { updated_at: draft.updated_at, updated_by: draft.updated_by } };
  } catch {
    return { data: live, isDraft: false, draftMeta: null };
  }
}
